import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Avatar, Input } from "antd";
import {
  ArrowLeftOutlined,
  CustomerServiceOutlined,
  LogoutOutlined,
  MenuOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
  SettingOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useAuth } from "contexts/AuthContext";
import paths from "routes/paths";
import { getUserDisplayName } from "utils/taskAssignee";
import WorkspacePicker from "workspaces/WorkspacePicker";
import { OLIVEEB_COLORS } from "workspaces/colors";

export default function Topbar({ onToggleMobile }) {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [ddOpen, setDdOpen] = useState(false);
  const menuRef = useRef(null);

  const displayName =
    getUserDisplayName(user) || user?.fullName || user?.username || "Account";
  const avatarSrc =
    typeof user?.profilePhoto === "string" &&
    /^https?:\/\//i.test(user.profilePhoto)
      ? user.profilePhoto
      : typeof user?.imageUrl === "string" &&
          /^https?:\/\//i.test(user.imageUrl)
        ? user.imageUrl
        : null;
  const avatarInitials = useMemo(() => {
    const first = String(user?.firstName || "").trim();
    const last = String(user?.lastName || "").trim();
    if (first || last) return `${first[0] || ""}${last[0] || ""}`.toUpperCase();
    const parts = String(displayName || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    return `${parts[0]?.[0] || ""}${parts[1]?.[0] || ""}`.toUpperCase() || "U";
  }, [displayName, user?.firstName, user?.lastName]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(event.target)) {
        setDdOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const profileMenuItems = [
    {
      label: "Profile",
      description: "View your Oliveeb identity",
      icon: <UserOutlined />,
      tone: "blue",
      onClick: () => {
        setDdOpen(false);
        navigate(paths.profile);
      },
    },
    {
      label: "Settings",
      description: "Tune your workspace experience",
      icon: <SettingOutlined />,
      tone: "yellow",
      onClick: () => {
        setDdOpen(false);
        navigate(paths.settings);
      },
    },
    {
      label: "Help",
      description: "Guides, answers, and tips",
      icon: <QuestionCircleOutlined />,
      tone: "blue",
      onClick: () => {
        setDdOpen(false);
        navigate(paths.help);
      },
    },
    {
      label: "Support",
      description: "Get help from Oliveeb",
      icon: <CustomerServiceOutlined />,
      tone: "orange",
      onClick: () => {
        setDdOpen(false);
        navigate(paths.support);
      },
    },
  ];

  const handleBack = (event) => {
    event.stopPropagation();
    if (window.history.length > 1 && location.key !== "default") {
      navigate(-1);
      return;
    }
    navigate(paths.home);
  };

  return (
    <header className="pm-t" onClick={() => setDdOpen(false)}>
      <div className="d-flex justify-content-between align-items-center w-100">
        <div className="d-flex align-items-center flex-grow-1 min-w-0">
          <button
            type="button"
            className="pm-mobile-toggle"
            onClick={onToggleMobile}
            aria-label="Open menu"
          >
            <MenuOutlined />
          </button>
          <button
            type="button"
            className="pm-back-btn"
            onClick={handleBack}
            aria-label="Go back"
            title="Back"
          >
            <ArrowLeftOutlined />
            <span>Back</span>
          </button>
          <Input
            prefix={<SearchOutlined style={{ color: "#cbd5e1", fontSize: 18 }} />}
            placeholder="Search..."
            className="pm-topsearch"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            allowClear
          />
        </div>
        <div className="ms-auto d-flex align-items-center">
          <div className="pm-acts">
            <div className="pm-ibtn" title="Notifications">
              <span>🔔</span>
              <span className="pm-ndot" />
            </div>

            <div className="pm-dv" />

            <WorkspacePicker />

            <div className="pm-dv" />

            <div
              className="pm-avw"
              ref={menuRef}
              onClick={(e) => {
                e.stopPropagation();
                setDdOpen((d) => !d);
              }}
            >
              <div className="pm-av">
                <Avatar
                  src={avatarSrc}
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "999px",
                    background: avatarSrc
                      ? undefined
                      : `linear-gradient(135deg, ${OLIVEEB_COLORS.oliveebDarkBlue}, ${OLIVEEB_COLORS.oliveebBlue})`,
                    color: OLIVEEB_COLORS.white,
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  {!avatarSrc ? avatarInitials : null}
                </Avatar>
              </div>

              {ddOpen && (
                <div className="pm-dd" onClick={(e) => e.stopPropagation()}>
                  <div className="pm-ddh">
                    <div className="pm-dd-avatar">
                      <Avatar
                        src={avatarSrc}
                        style={{
                          width: "100%",
                          height: "100%",
                          borderRadius: "999px",
                          background: avatarSrc
                            ? undefined
                            : `linear-gradient(135deg, ${OLIVEEB_COLORS.oliveebGreen}, ${OLIVEEB_COLORS.oliveebBlue})`,
                          color: OLIVEEB_COLORS.white,
                          fontSize: 16,
                          fontWeight: 900,
                        }}
                      >
                        {!avatarSrc ? avatarInitials : null}
                      </Avatar>
                    </div>
                    <div className="pm-dd-user">
                      <div className="pm-dd-kicker">Signed in as</div>
                      <div className="pm-ddn">{displayName}</div>
                      <div className="pm-dde">{user?.email || "Oliveeb account"}</div>
                    </div>
                  </div>
                  <div className="pm-dd-list">
                    {profileMenuItems.map((item) => (
                      <div
                        key={item.label}
                        className="pm-ddi pm-dd-action"
                        onClick={() => item.onClick?.()}
                        data-tone={item.tone}
                        style={{ cursor: item.onClick ? "pointer" : "default" }}
                      >
                        <span className="pm-dd-ico">{item.icon}</span>
                        <span className="pm-dd-copy">
                          <span className="pm-dd-label">{item.label}</span>
                          <span className="pm-dd-desc">{item.description}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                  <div
                    className="pm-ddi pm-dd-action dng mt-2"
                    onClick={() => logout(false, paths.landing)}
                    style={{ cursor: "pointer" }}
                  >
                    <span className="pm-dd-ico"><LogoutOutlined /></span>
                    <span className="pm-dd-copy">
                      <span className="pm-dd-label">Sign out</span>
                      <span className="pm-dd-desc">End this browser session</span>
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
